import { Box, Button, Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonTextInput } from 'components/common/form/text';
import { CommonProgress } from 'components/common/progress';
import { LandingFormWrapper } from 'components/main/landing/forms/wrapper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IResetPasswordPayload } from 'lib_ts/interfaces/common/i-login';
import { useCallback, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import { useAuthStore } from 'stores/use-auth-store';

const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 8,
  CONTAINS_NUMBER: true,
  CONTAINS_SYMBOL: true,
};

const invalidPasswordReasons = (value: string): string[] => {
  const output: string[] = [];

  if (value.match(/\s/)) {
    output.push('not contain any whitespace');
  }

  if (value.length < PASSWORD_REQUIREMENTS.MIN_LENGTH) {
    output.push(
      `contain at least ${PASSWORD_REQUIREMENTS.MIN_LENGTH} characters`
    );
  }

  if (PASSWORD_REQUIREMENTS.CONTAINS_NUMBER && !value.match(/\d/)) {
    output.push('contain at least one number');
  }

  if (PASSWORD_REQUIREMENTS.CONTAINS_SYMBOL && !value.match(/[^a-zA-Z\d\s]/)) {
    output.push(
      'contain at least one special symbol, for example `!@#$%^&*_+-=`'
    );
  }

  return output;
};

export const ResetPasswordForm = () => {
  const [payload, setPayload] = useState<IResetPasswordPayload>(() => {
    const query = new URLSearchParams(window.location.search);

    return {
      email: query.get('email')?.replace(' ', '+') ?? '',
      code: query.get('code') ?? '',
      password: '',
    };
  });

  const [confirmPassword, setConfirmPassword] = useState('');
  const [redirect, setRedirect] = useState(false);

  const loading = useAuthStore((s) => s.loading);
  const resetPassword = useAuthStore((s) => s.resetPassword);

  const goToLogin = () => {
    setRedirect(true);
  };

  const submit = useCallback(() => {
    if (!payload.password) {
      NotifyHelper.warning({
        message_md: 'Please enter a password and try again.',
      });
      return;
    }

    const reasons = invalidPasswordReasons(payload.password);

    if (reasons.length > 0) {
      NotifyHelper.warning({
        message_md:
          'Your password must:\n' + reasons.map((m) => ` - ${m}`).join('\n'),
        delay_ms: 0,
        buttons: [
          {
            label: 'common.dismiss',
            onClick: () => {
              // nothing
            },
            dismissAfterClick: true,
          },
        ],
      });
      return;
    }

    if (confirmPassword !== payload.password) {
      NotifyHelper.warning({
        message_md: 'Please confirm your password and try again.',
      });
      return;
    }

    const requestPasswordReset = async () => {
      const response = await resetPassword(payload);
      if (!response) {
        return;
      }

      NotifyHelper.success({
        message_md:
          'Your password has been reset. You will be redirected to the legacy login page shortly...',
        delay_ms: 0,
        buttons: [
          {
            label: 'Return to login',
            onClick: goToLogin,
          },
        ],
      });

      setTimeout(() => {
        goToLogin();
      }, 2_000);
    };

    requestPasswordReset();
  }, [payload, confirmPassword, resetPassword]);

  const onKeyUp = useCallback(
    (event: any) => {
      if (event.code === 'Enter') {
        submit();
      }
    },
    [submit]
  );

  const renderContent = () => {
    if (!payload.email || !payload.code) {
      return (
        <Flex justify="center">
          <Text>Could not parse email and/or code values.</Text>
        </Flex>
      );
    }

    if (loading) {
      return <CommonProgress value={80} color={RADIX.COLOR.INFO} />;
    }

    return (
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        <Box>
          <CommonTextInput
            id="reset-password-email"
            value={payload.email}
            type="email"
            placeholder="user@trajektsports.com"
            onKeyUp={onKeyUp}
            autoComplete="username"
            disabled
            onChange={(v) => {
              setPayload((prev) => ({
                ...prev,
                email: v ?? '',
              }));
            }}
          />
        </Box>
        <Box>
          <CommonTextInput
            id="reset-password-password"
            controlId="password"
            value={payload.password}
            type="password"
            placeholder="New password"
            onKeyUp={onKeyUp}
            autoComplete="current-password"
            disabled={loading}
            onChange={(v) => {
              setPayload((prev) => ({
                ...prev,
                password: v ?? '',
              }));
            }}
          />
        </Box>

        <Box>
          <CommonTextInput
            id="reset-password-confirm"
            controlId="confirm-password"
            value={confirmPassword}
            type="password"
            placeholder="Confirm new password"
            onKeyUp={onKeyUp}
            disabled={loading}
            onChange={(v) => {
              setConfirmPassword(v ?? '');
            }}
          />
        </Box>

        <CommonFormGrid columns={2}>
          <Box>
            <Button
              color={RADIX.COLOR.INFO}
              className="btn-block"
              disabled={loading}
              onClick={submit}
            >
              Reset password
            </Button>
          </Box>
          <Box>
            <Button className="btn-block" onClick={goToLogin}>
              Return to login
            </Button>
          </Box>
        </CommonFormGrid>
      </Flex>
    );
  };

  if (redirect) {
    return <Navigate to={paths.login.legacy} />;
  }

  const header = loading
    ? 'Please wait...'
    : !payload.email || !payload.code
      ? 'Invalid reset password URL.'
      : 'Please reset your password for legacy login.';

  return (
    <ErrorBoundary identifier="ResetPasswordForm" hideIntercom>
      <LandingFormWrapper header={header}>
        <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
          {renderContent()}
        </Flex>
      </LandingFormWrapper>
    </ErrorBoundary>
  );
};
